import React, { useState, useEffect, useMemo } from "react";
import {
  GraduationCap,
  Search,
  Filter,
  Eye,
  RefreshCw,
  ChevronDown,
  X,
  Loader2,
  MapPin,
  Phone,
  Mail,
  CheckCircle,
  Clock,
  BookOpen,
  User,
  Calendar,
  Shield,
  Power,
  AlertTriangle,
  Pencil,
  Upload,
  ArrowLeft,
} from "lucide-react";
import studentsService from "../../../api/StudentsService";
import { toast } from "react-toastify";
import Loader from "../../../components/misc/Loader";
import { ghanaRegions, ghanaLocationsByRegion } from "../../../data/ghanaRegionsAndLocations";
import SearchableSelect from "../../../components/SearchableSelect";
import { useSearchParams, useNavigate } from "react-router-dom";

// Helper to normalize date strings (handles ISO format, YYYY-MM-DD, etc.)
const normalizeDateString = (dateStr) => {
  if (!dateStr) return dateStr;
  // If it's an ISO string like "2026-01-23T00:00:00.000000Z", extract date part
  if (typeof dateStr === 'string' && dateStr.includes('T')) {
    return dateStr.split('T')[0];
  }
  // If already in YYYY-MM-DD format, return as is
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  return dateStr;
};

// Map API response to consistent format
const mapStudentFromApi = (s) => {
  if (!s) return s;
  const status =
    s.account_status === 1
      ? "Active"
      : s.account_status === 0
      ? "Pending"
      : "Inactive";
  
  const issueDate = s.card_issue_date || s.issue_date || s.studentCardIssueDate;
  const expiryDate = s.card_expiry_date || s.expiry_date || s.studentCardExpiryDate;
  
  return {
    ...s,
    firstName: s.first_name || s.firstName,
    lastName: s.last_name || s.lastName,
    email: s.email,
    phone: s.phone_number || s.phone,
    region: s.region,
    location: s.location,
    schoolName: s.school_name || s.schoolName,
    studentId: s.student_school_id || s.studentId,
    studentCardIssueDate: normalizeDateString(issueDate),
    studentCardExpiryDate: normalizeDateString(expiryDate),
    agentCode: s.agent?.agent_code || s.agent_code || s.agent_slug || "",
    agentName: s.agent
      ? `${s.agent.first_name || ""} ${s.agent.last_name || ""}`.trim()
      : "",
    agentEmail: s.agent?.email || "",
    agentPhone: s.agent?.phone_number || "",
    guardianName: s.guardian_name || null,
    guardianPhone: s.guardian_phone_number || null,
    studentCardFront: s.id_card_front || s.studentCardFront,
    studentCardBack: s.id_card_back || s.studentCardBack,
    dob: s.dob || null,
    gender: s.gender || null,
    status,
    slug: s.slug || s.id,
  };
};

const RegisteredStudents = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const agentSlug = searchParams.get("agent");
  const [agentInfo, setAgentInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // View details modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Activation/Deactivation modal state
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [studentToActivate, setStudentToActivate] = useState(null);
  const [activating, setActivating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [studentToEdit, setStudentToEdit] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneDisplay: "",
    gender: "",
    dob: "",
    region: "",
    location: "",
    customLocation: "",
    schoolName: "",
    studentId: "",
    studentCardIssueDate: "",
    studentCardExpiryDate: "",
    studentCardFront: null,
    studentCardBack: null,
  });
  const [originalImages, setOriginalImages] = useState({
    studentCardFront: null,
    studentCardBack: null,
  });

  // Filter and search students
  const filteredStudents = useMemo(() => {
    return students
      .filter((student) => {
        const matchesSearch =
          student.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.phone?.includes(searchTerm) ||
          student.region?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.schoolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.studentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.agentCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.agentName?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus =
          statusFilter === "all" ||
          student.status?.toLowerCase() === statusFilter.toLowerCase();

        const matchesRegion =
          regionFilter === "all" ||
          student.region?.toLowerCase().includes(regionFilter.toLowerCase());

        return matchesSearch && matchesStatus && matchesRegion;
      })
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "name":
            aValue = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
            bValue = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
            break;
          case "email":
            aValue = (a.email || "").toLowerCase();
            bValue = (b.email || "").toLowerCase();
            break;
          case "location":
            aValue = (a.location || "").toLowerCase();
            bValue = (b.location || "").toLowerCase();
            break;
          case "school":
            aValue = (a.schoolName || "").toLowerCase();
            bValue = (b.schoolName || "").toLowerCase();
            break;
          case "status":
            aValue = a.status?.toLowerCase() || "";
            bValue = b.status?.toLowerCase() || "";
            break;
          default:
            aValue = a[sortBy] || "";
            bValue = b[sortBy] || "";
        }

        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
  }, [students, searchTerm, statusFilter, regionFilter, sortBy, sortOrder]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchStudents = async () => {
      setLoading(true);
      try {
        let response;
        let studentsArray = [];

        if (agentSlug) {
          // Fetch students for specific agent
          response = await studentsService.getStudentsByAgent(agentSlug);
        } else {
          // Fetch all students
          response = await studentsService.getAllStudents();
        }

        if (response.status && response.data) {
          // Handle different response structures
          const data = response.data;

          if (Array.isArray(data)) {
            studentsArray = data;
          } else if (typeof data === 'object' && data !== null) {
            // Handle object with numeric keys (like the example response)
            if (Object.keys(data).some(key => !isNaN(key))) {
              studentsArray = Object.values(data);
            } else if (data?.data) {
              if (Array.isArray(data.data)) {
                studentsArray = data.data;
              } else if (data.data?.data && Array.isArray(data.data.data)) {
                studentsArray = data.data.data;
              } else if (typeof data.data === 'object' && data.data !== null) {
                // Handle object with numeric keys in data.data
                if (Object.keys(data.data).some(key => !isNaN(key))) {
                  studentsArray = Object.values(data.data);
                }
              }
            } else if (data?.status_code === 200 && data?.data) {
              if (Array.isArray(data.data)) {
                studentsArray = data.data;
              } else if (data.data?.data && Array.isArray(data.data.data)) {
                studentsArray = data.data.data;
              } else if (typeof data.data === 'object' && data.data !== null) {
                // Handle object with numeric keys
                if (Object.keys(data.data).some(key => !isNaN(key))) {
                  studentsArray = Object.values(data.data);
                }
              }
            }
          }

          const mappedStudents = studentsArray.map(mapStudentFromApi);
          setStudents(mappedStudents);

          // Extract agent info from first student if viewing agent-specific students
          if (agentSlug && mappedStudents.length > 0 && mappedStudents[0].agentName) {
            setAgentInfo({
              name: mappedStudents[0].agentName,
              code: mappedStudents[0].agentCode,
              email: mappedStudents[0].agentEmail,
              phone: mappedStudents[0].agentPhone,
            });
          }
        } else {
          toast.error(response.message || "Failed to fetch students");
          setStudents([]);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            "An error occurred while fetching students"
        );
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [agentSlug]);

  // Calculate stats
  const totalStudents = students.length;
  const activeStudents = students.filter(
    (s) => s.status?.toLowerCase() === "active"
  ).length;
  const pendingStudents = students.filter(
    (s) => s.status?.toLowerCase() === "pending"
  ).length;
  const uniqueSchools = new Set(
    students.map((s) => s.schoolName).filter(Boolean)
  ).size;

  const getStatusBadge = (student) => {
    const status = student.status?.toLowerCase() || "inactive";
    if (status === "active") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
          <CheckCircle className="mr-1 w-3 h-3" />
          Active
        </span>
      );
    } else if (status === "pending") {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full">
          <Clock className="mr-1 w-3 h-3" />
          Pending
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
          <Clock className="mr-1 w-3 h-3" />
          Inactive
        </span>
      );
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      let response;
      let studentsArray = [];

      if (agentSlug) {
        response = await studentsService.getStudentsByAgent(agentSlug);
      } else {
        response = await studentsService.getAllStudents();
      }

      if (response.status && response.data) {
        const data = response.data;

        if (Array.isArray(data)) {
          studentsArray = data;
        } else if (typeof data === 'object' && data !== null) {
          if (Object.keys(data).some(key => !isNaN(key))) {
            studentsArray = Object.values(data);
          } else if (data?.data) {
            if (Array.isArray(data.data)) {
              studentsArray = data.data;
            } else if (data.data?.data && Array.isArray(data.data.data)) {
              studentsArray = data.data.data;
            } else if (typeof data.data === 'object' && data.data !== null) {
              if (Object.keys(data.data).some(key => !isNaN(key))) {
                studentsArray = Object.values(data.data);
              }
            }
          } else if (data?.status_code === 200 && data?.data) {
              if (Array.isArray(data.data)) {
                studentsArray = data.data;
              } else if (data.data?.data && Array.isArray(data.data.data)) {
                studentsArray = data.data.data;
              } else if (typeof data.data === 'object' && data.data !== null) {
                if (Object.keys(data.data).some(key => !isNaN(key))) {
                  studentsArray = Object.values(data.data);
                }
              }
            }
        }

        const mappedStudents = studentsArray.map(mapStudentFromApi);
        setStudents(mappedStudents);

        if (agentSlug && mappedStudents.length > 0 && mappedStudents[0].agentName) {
          setAgentInfo({
            name: mappedStudents[0].agentName,
            code: mappedStudents[0].agentCode,
            email: mappedStudents[0].agentEmail,
            phone: mappedStudents[0].agentPhone,
          });
        }

        toast.success("Students data refreshed successfully");
      } else {
        toast.error(response.message || "Failed to refresh students");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "An error occurred while refreshing students"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedStudent(null);
  };

  const handleActivateStudent = (student) => {
    setStudentToActivate(student);
    setIsDeactivating(student.status?.toLowerCase() === "active");
    setShowActivateModal(true);
  };

  const closeActivateModal = () => {
    setShowActivateModal(false);
    setStudentToActivate(null);
    setActivating(false);
    setIsDeactivating(false);
  };

  const confirmActivate = async () => {
    if (!studentToActivate) return;

    setActivating(true);
    try {
      const response = await studentsService.activateStudent(
        studentToActivate.slug
      );
      if (response.status) {
        const isCurrentlyActive =
          studentToActivate.status?.toLowerCase() === "active";
        toast.success(
          response.message ||
            (isCurrentlyActive
              ? "Student deactivated successfully"
              : "Student activated successfully")
        );
        // Refresh the students list
        await handleRefresh();
        closeActivateModal();
      } else {
        toast.error(
          response.message ||
            (isDeactivating
              ? "Failed to deactivate student"
              : "Failed to activate student")
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (isDeactivating
            ? "An error occurred while deactivating student"
            : "An error occurred while activating student")
      );
    } finally {
      setActivating(false);
    }
  };

  // Edit handlers
  const handleEditStudent = (student) => {
    setStudentToEdit(student);
    
    const parseDOB = (dobString) => {
      if (!dobString || dobString.length !== 8) return "";
      const day = dobString.slice(0, 2);
      const month = dobString.slice(2, 4);
      const year = dobString.slice(4, 8);
      return `${year}-${month}-${day}`;
    };

    const parseDateForInput = (dateString) => {
      if (!dateString) return "";
      try {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      } catch {
        return "";
      }
    };

    const formatPhoneForDisplay = (phone) => {
      if (!phone) return "";
      const p = String(phone).replace(/\D/g, "");
      if (p.startsWith("233")) return `0${p.slice(3)}`;
      return phone;
    };

    const rawDob = student.dob || "";
    const dobForForm = rawDob && rawDob.length === 8 ? parseDOB(rawDob) : parseDateForInput(rawDob);

    // Handle location - check if it's in the standard locations or custom
    const studentRegion = student.region || "";
    const studentLocation = student.location || "";
    let locationValue = studentLocation;
    let customLocationValue = "";

    if (studentRegion && studentLocation) {
      const standardLocations = ghanaLocationsByRegion[studentRegion] || [];
      if (!standardLocations.includes(studentLocation)) {
        locationValue = "Other";
        customLocationValue = studentLocation;
      }
    }

    setEditFormData({
      firstName: student.firstName || "",
      lastName: student.lastName || "",
      email: student.email || "",
      phone: student.phone || "",
      phoneDisplay: formatPhoneForDisplay(student.phone || ""),
      gender: student.gender ? student.gender.charAt(0).toUpperCase() + student.gender.slice(1).toLowerCase() : "",
      dob: dobForForm,
      region: studentRegion,
      location: locationValue,
      customLocation: customLocationValue,
      schoolName: student.schoolName || "",
      studentId: student.studentId || "",
      studentCardIssueDate: parseDateForInput(student.studentCardIssueDate || ""),
      studentCardExpiryDate: parseDateForInput(student.studentCardExpiryDate || ""),
      studentCardFront: student.studentCardFront || null,
      studentCardBack: student.studentCardBack || null,
    });

    setOriginalImages({
      studentCardFront: student.studentCardFront || null,
      studentCardBack: student.studentCardBack || null,
    });

    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setStudentToEdit(null);
    setIsSubmitting(false);
    setEditFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      phoneDisplay: "",
      gender: "",
      dob: "",
      region: "",
      location: "",
      customLocation: "",
      schoolName: "",
      studentId: "",
      studentCardIssueDate: "",
      studentCardExpiryDate: "",
      studentCardFront: null,
      studentCardBack: null,
    });
    setOriginalImages({
      studentCardFront: null,
      studentCardBack: null,
    });
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditPhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, "");
    let formatted = digitsOnly.startsWith("233") ? `0${digitsOnly.slice(3)}` : digitsOnly;
    if (formatted.length > 10) formatted = formatted.slice(0, 10);
    setEditFormData((prev) => ({
      ...prev,
      phoneDisplay: formatted,
      phone: formatted.startsWith("0") ? `233${formatted.slice(1)}` : formatted,
    }));
  };

  const isPhoneValid = () => {
    if (!editFormData.phoneDisplay) return false;
    if (editFormData.phoneDisplay.startsWith("233")) return editFormData.phoneDisplay.length === 12;
    if (editFormData.phoneDisplay.startsWith("0")) return editFormData.phoneDisplay.length === 10;
    return false;
  };

  const handleImageUpload = (field, file) => {
    const reader = new FileReader();
    reader.onload = (e) => setEditFormData((prev) => ({ ...prev, [field]: e.target.result }));
    reader.readAsDataURL(file);
  };

  const removeImage = (field) => {
    setEditFormData((prev) => ({ ...prev, [field]: null }));
  };

  const isImageChanged = (current, original) => {
    if (!current) return false;
    if (!original) return current.startsWith("data:image");
    return current.startsWith("data:image") && current !== original;
  };

  const formatDOB = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  };

  const isFormValid = () => {
    const required = ["firstName", "lastName", "email", "phone", "gender", "dob", "region", "location"];
    const filled = required.every((field) => {
      if (field === "location") return editFormData.location || (editFormData.location === "Other" && editFormData.customLocation);
      return editFormData[field]?.trim();
    });
    return filled && isPhoneValid();
  };

  const createUpdatePayload = () => {
    const payload = {
      first_name: editFormData.firstName,
      last_name: editFormData.lastName,
      email: editFormData.email,
      phone_number: editFormData.phone,
      location: editFormData.location === "Other" ? editFormData.customLocation : editFormData.location,
      dob: formatDOB(editFormData.dob),
      region: editFormData.region,
      gender: editFormData.gender.toLowerCase(),
    };
    if (editFormData.schoolName?.trim()) payload.school_name = editFormData.schoolName.trim();
    if (editFormData.studentId?.trim()) payload.student_school_id = editFormData.studentId.trim();
    if (editFormData.studentCardIssueDate) payload.card_issue_date = editFormData.studentCardIssueDate;
    if (editFormData.studentCardExpiryDate) payload.card_expiry_date = editFormData.studentCardExpiryDate;
    if (editFormData.studentCardFront) {
      payload.id_card_front = isImageChanged(editFormData.studentCardFront, originalImages.studentCardFront)
        ? editFormData.studentCardFront
        : originalImages.studentCardFront;
    }
    if (editFormData.studentCardBack) {
      payload.id_card_back = isImageChanged(editFormData.studentCardBack, originalImages.studentCardBack)
        ? editFormData.studentCardBack
        : originalImages.studentCardBack;
    }
    return payload;
  };

  const handleEditSubmit = async () => {
    if (!isFormValid()) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!studentToEdit?.slug) {
      toast.error("Student identifier missing");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = createUpdatePayload();
      const response = await studentsService.updateStudent(studentToEdit.slug, payload);

      if (response.status) {
        toast.success(response.message || "Student updated successfully!");
        await handleRefresh();
        closeEditModal();
      } else {
        toast.error(response.message || "Failed to update student");
        if (response.validationErrors) console.error("Validation errors:", response.validationErrors);
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Update error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && students.length === 0) {
    return <Loader fullScreen={true} text="Loading students..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Container */}
      <div className="px-3 py-4 space-y-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8 sm:space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex-shrink-0">
            {agentSlug && agentInfo ? (
              <>
                <div className="flex items-center mb-2">
                  <button
                    onClick={() => navigate("/admin/agents")}
                    className="p-2 mr-3 text-gray-600 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
                    title="Back to Agents"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h1 className="flex items-center text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl xl:text-4xl">
                    <GraduationCap className="flex-shrink-0 mr-2 w-6 h-6 text-emerald-600 sm:w-7 sm:h-7 lg:w-8 lg:h-8 sm:mr-3" />
                    <span className="truncate">Registered Students</span>
                  </h1>
                </div>
                <p className="mt-1 text-sm text-gray-600 sm:text-base sm:mt-2">
                  Students registered by{" "}
                  <span className="font-semibold text-emerald-600">
                    {agentInfo.name}
                  </span>
                  {agentInfo.code && (
                    <span className="px-2 py-1 ml-2 font-mono text-xs text-emerald-800 bg-emerald-100 rounded">
                      {agentInfo.code}
                    </span>
                  )}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center mb-2">
                  <button
                    onClick={() => navigate(-1)}
                    className="p-2 mr-3 text-gray-600 rounded-lg transition-colors hover:text-gray-900 hover:bg-gray-100"
                    title="Go Back"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h1 className="flex items-center text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl xl:text-4xl">
                    <GraduationCap className="flex-shrink-0 mr-2 w-6 h-6 text-emerald-600 sm:w-7 sm:h-7 lg:w-8 lg:h-8 sm:mr-3" />
                    <span className="truncate">Registered Students</span>
                  </h1>
                </div>
                <p className="mt-1 text-sm text-gray-600 sm:text-base sm:mt-2">
                  Manage and monitor all registered student clients
                </p>
              </>
            )}
          </div>

          {/* Mobile Header Actions */}
          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleRefresh}
              className="bg-gray-100 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <RefreshCw className="flex-shrink-0 mr-2 w-4 h-4" />
              <span className="hidden xs:inline">Refresh</span>
              <span className="xs:hidden">Reload</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-3 xs:grid-cols-2 lg:grid-cols-4 sm:gap-4 lg:gap-6">
          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 sm:rounded-xl sm:p-5 lg:p-6 hover:shadow-md">
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 truncate sm:text-sm">
                  Total Students
                </p>
                <p className="mt-1 text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl">
                  {totalStudents}
                </p>
              </div>
              <div className="flex-shrink-0 p-2 ml-3 bg-blue-100 rounded-lg sm:p-3">
                <GraduationCap className="w-4 h-4 text-blue-600 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 sm:rounded-xl sm:p-5 lg:p-6 hover:shadow-md">
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 truncate sm:text-sm">
                  Active
                </p>
                <p className="mt-1 text-xl font-bold text-green-600 sm:text-2xl lg:text-3xl">
                  {activeStudents}
                </p>
                <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                  {totalStudents > 0
                    ? Math.round((activeStudents / totalStudents) * 100)
                    : 0}
                  % of total
                </p>
              </div>
              <div className="flex-shrink-0 p-2 ml-3 bg-green-100 rounded-lg sm:p-3">
                <CheckCircle className="w-4 h-4 text-green-600 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 sm:rounded-xl sm:p-5 lg:p-6 hover:shadow-md">
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 truncate sm:text-sm">
                  Pending
                </p>
                <p className="mt-1 text-xl font-bold text-yellow-600 sm:text-2xl lg:text-3xl">
                  {pendingStudents}
                </p>
                <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                  {totalStudents > 0
                    ? Math.round((pendingStudents / totalStudents) * 100)
                    : 0}
                  % of total
                </p>
              </div>
              <div className="flex-shrink-0 p-2 ml-3 bg-yellow-100 rounded-lg sm:p-3">
                <Clock className="w-4 h-4 text-yellow-600 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
            </div>
          </div>

          <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow duration-300 sm:rounded-xl sm:p-5 lg:p-6 hover:shadow-md">
            <div className="flex justify-between items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-600 truncate sm:text-sm">
                  Schools
                </p>
                <p className="mt-1 text-xl font-bold text-purple-600 sm:text-2xl lg:text-3xl">
                  {uniqueSchools}
                </p>
                <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                  Unique institutions
                </p>
              </div>
              <div className="flex-shrink-0 p-2 ml-3 bg-purple-100 rounded-lg sm:p-3">
                <BookOpen className="w-4 h-4 text-purple-600 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm sm:rounded-xl sm:p-6">
          {/* Mobile Filter Toggle */}
          <div className="flex justify-between items-center mb-4 lg:hidden">
            <h3 className="flex items-center text-base font-semibold text-gray-900">
              <Filter className="mr-2 w-4 h-4 text-emerald-600" />
              Filters
            </h3>
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 touch-manipulation"
            >
              <span>Filters</span>
              <ChevronDown
                className={`w-4 h-4 ml-2 transition-transform ${
                  showMobileFilters ? "rotate-180" : ""}`}
              />
            </button>
          </div>

          {/* Desktop Filter Header */}
          <div className="hidden justify-between items-center mb-6 lg:flex">
            <h3 className="flex items-center text-lg font-semibold text-gray-900">
              <Filter className="mr-2 w-5 h-5 text-emerald-600" />
              Filters & Search
            </h3>
            {/* <button className="text-sm font-medium text-emerald-600 transition-colors hover:text-emerald-700">
              Export Data
            </button> */}
          </div>

          {/* Filter Controls */}
          <div
            className={`space-y-4 ${
              showMobileFilters ? "block" : "hidden lg:block"
            }`}
          >
            {/* Search */}
            <div className="relative">
              <Search className="absolute top-3 left-3 w-4 h-4 text-gray-400 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search students by name, email, school, ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="py-3 pr-4 pl-10 w-full text-sm rounded-lg border border-gray-300 transition-all sm:text-base focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-3 text-sm bg-white rounded-lg border border-gray-300 appearance-none sm:px-4 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="pending">Pending Only</option>
                <option value="inactive">Inactive Only</option>
              </select>

              {/* Region Filter */}
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="px-3 py-3 text-sm bg-white rounded-lg border border-gray-300 appearance-none sm:px-4 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="all">All Regions</option>
                {ghanaRegions.map((region) => (
                  <option key={region} value={region.toLowerCase()}>
                    {region}
                  </option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-3 text-sm bg-white rounded-lg border border-gray-300 appearance-none sm:px-4 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="name">Sort by Name</option>
                <option value="email">Sort by Email</option>
                <option value="location">Sort by Location</option>
                <option value="school">Sort by School</option>
                <option value="status">Sort by Status</option>
              </select>

              {/* Sort Order */}
              <button
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
                className="flex justify-center items-center px-3 py-3 text-sm bg-gray-100 rounded-lg border border-gray-300 transition-colors sm:px-4 hover:bg-gray-200 active:bg-gray-300 touch-manipulation"
              >
                {sortOrder === "asc" ? "↓" : "↑"}{" "}
                {sortOrder === "asc" ? "Ascending" : "Descending"}
              </button>
            </div>
          </div>

          {/* Clear Filters Button */}
          {searchTerm || statusFilter !== "all" || regionFilter !== "all" ? (
            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setRegionFilter("all");
                }}
                className="px-3 py-2 text-sm text-red-600 rounded-lg transition-colors hover:text-red-700 hover:bg-red-50"
              >
                Clear Filters
              </button>
            </div>
          ) : null}
        </div>

        {/* Students Display */}
        <div className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm sm:rounded-xl">
          {/* Table Header */}
          <div className="px-3 py-3 bg-gray-50 border-b border-gray-200 sm:px-4 lg:px-6 sm:py-4">
            <div className="flex justify-between items-center">
              <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
                Student List ({filteredStudents.length}{" "}
                {filteredStudents.length === 1 ? "student" : "students"} found)
              </h3>
              <div className="hidden text-xs text-gray-500 sm:block sm:text-sm">
                Showing {filteredStudents.length} of {totalStudents} students
              </div>
            </div>
          </div>

          {/* Desktop View */}
          <div className="hidden overflow-x-auto lg:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Student Details
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Contact Info
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Location
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    School Information
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap">
                    Agent Code
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map((student) => (
                  <tr
                    key={student.slug || student.id}
                    className="transition-colors duration-150 hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full shadow-sm">
                          <span className="text-sm font-medium text-white">
                            {student.firstName?.charAt(0) || "S"}
                            {student.lastName?.charAt(0) || "T"}
                          </span>
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {student.firstName} {student.lastName}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            ID: {student.studentId || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {student.email ? (
                          <div className="flex items-center text-sm text-gray-900 truncate">
                            <Mail className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                            <span className="truncate">{student.email}</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <Mail className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                              Not Available
                            </span>
                          </div>
                        )}
                        <div className="flex items-center text-sm text-gray-500 truncate">
                          <Phone className="flex-shrink-0 mr-2 w-3 h-3 text-gray-400" />
                          <span className="truncate">{student.phone || "N/A"}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center min-w-0">
                        <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                        <div className="min-w-0">
                          {student.location ? (
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {student.location}
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                              Not Available
                            </span>
                          )}
                          {student.region ? (
                            <div className="text-sm text-gray-500 truncate">
                              {student.region}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {student.schoolName || "N/A"}
                        </div>
                        {student.studentCardIssueDate && (
                          <div className="text-xs text-gray-500">
                            Issued:{" "}
                            {new Date(
                              student.studentCardIssueDate
                            ).toLocaleDateString()}
                          </div>
                        )}
                        {student.studentCardExpiryDate && (
                          <div className="text-xs text-gray-500">
                            Expires:{" "}
                            {new Date(
                              student.studentCardExpiryDate
                            ).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      {getStatusBadge(student)}
                    </td>
                    <td className="px-6 py-4">
                      {student.agentCode ? (
                        <span className="px-3 py-1 font-mono text-sm font-semibold text-emerald-800 bg-emerald-100 rounded-lg">
                          {student.agentCode}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end items-center space-x-1">
                        <button
                          onClick={() => handleViewStudent(student)}
                          className="p-2 text-gray-400 rounded-lg transition-colors hover:text-emerald-600 hover:bg-emerald-50 touch-manipulation"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditStudent(student)}
                          className="p-2 text-gray-400 rounded-lg transition-colors hover:text-blue-600 hover:bg-blue-50 touch-manipulation"
                          title="Edit Student"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleActivateStudent(student)}
                          className={`p-2 rounded-lg transition-colors touch-manipulation ${
                            student.status?.toLowerCase() === "active"
                              ? "text-red-500 hover:text-red-600 hover:bg-red-50"
                              : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                          }`}
                          title={
                            student.status?.toLowerCase() === "active"
                              ? "Deactivate Student"
                              : "Activate Student"
                          }
                        >
                          <Power className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="divide-y divide-gray-200 lg:hidden">
            {filteredStudents.map((student) => (
              <div
                key={student.slug || student.id}
                className="p-4 transition-colors hover:bg-gray-50"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex flex-1 items-center min-w-0">
                    <div className="flex flex-shrink-0 justify-center items-center mr-3 w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full shadow-sm">
                      <span className="text-sm font-medium text-white">
                        {student.firstName?.charAt(0) || "S"}
                        {student.lastName?.charAt(0) || "T"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold text-gray-900 truncate">
                        {student.firstName} {student.lastName}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {student.schoolName || "N/A"}
                      </div>
                      <div className="mt-1">{getStatusBadge(student)}</div>
                    </div>
                  </div>
                  <div className="flex ml-2 space-x-1">
                    <button
                      onClick={() => handleViewStudent(student)}
                      className="p-2 text-gray-400 rounded-lg transition-colors hover:text-emerald-600 hover:bg-emerald-50 touch-manipulation"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEditStudent(student)}
                      className="p-2 text-gray-400 rounded-lg transition-colors hover:text-blue-600 hover:bg-blue-50 touch-manipulation"
                      title="Edit Student"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleActivateStudent(student)}
                      className={`p-2 rounded-lg transition-colors touch-manipulation ${
                        student.status?.toLowerCase() === "active"
                          ? "text-red-500 hover:text-red-600 hover:bg-red-50"
                          : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                      }`}
                      title={
                        student.status?.toLowerCase() === "active"
                          ? "Deactivate Student"
                          : "Activate Student"
                      }
                    >
                      <Power className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {/* Contact Info */}
                  <div className="space-y-2">
                    {student.email && (
                      <div className="flex items-center text-sm">
                        <Mail className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                        <span className="text-gray-900 truncate">
                          {student.email}
                        </span>
                      </div>
                    )}
                    {student.phone && (
                      <div className="flex items-center text-sm">
                        <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                        <span className="text-gray-900 truncate">
                          {student.phone}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Location */}
                  {(student.location || student.region) && (
                    <div className="space-y-2">
                      {student.location && (
                        <div className="flex items-center text-sm">
                          <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                          <span className="text-gray-900 truncate">
                            {student.location}
                          </span>
                        </div>
                      )}
                      {student.region && (
                        <div className="ml-6 text-sm text-gray-500 truncate">
                          {student.region}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Student ID */}
                  {student.studentId && (
                    <div className="pt-2 border-t border-gray-100">
                      <div className="text-sm text-gray-600">
                        Student ID:{" "}
                        <span className="font-mono">{student.studentId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredStudents.length === 0 && !loading && (
            <div className="py-12 text-center lg:py-16">
              <GraduationCap className="mx-auto w-12 h-12 text-gray-300 lg:h-16 lg:w-16" />
              <h3 className="mt-4 text-base font-medium text-gray-900 lg:text-lg">
                No students found
              </h3>
              <p className="px-4 mx-auto mt-2 max-w-sm text-sm text-gray-500 lg:text-sm">
                {searchTerm || statusFilter !== "all" || regionFilter !== "all"
                  ? "Try adjusting your search or filter criteria to find students."
                  : "No students have been registered yet."}
              </p>
            </div>
          )}
        </div>

        {/* Mobile Stats */}
        <div className="text-center lg:hidden">
          <div className="inline-block px-3 py-2 text-xs text-gray-500 bg-gray-100 rounded-lg">
            Showing {filteredStudents.length} of {totalStudents} students
          </div>
        </div>

        {/* View Details Modal */}
        {showViewModal && selectedStudent && (
          <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
              onClick={closeViewModal}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="px-6 py-5 bg-gradient-to-r from-emerald-600 to-blue-600">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <div className="flex justify-center items-center w-14 h-14 bg-white bg-opacity-20 rounded-full border-2 border-white border-opacity-30 backdrop-blur-sm">
                      <GraduationCap className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">
                        {selectedStudent.firstName} {selectedStudent.lastName}
                      </h3>
                      <p className="text-emerald-100 text-sm mt-0.5">Student Information</p>
                    </div>
                  </div>
                  <button
                    onClick={closeViewModal}
                    className="p-2 text-white rounded-lg transition-all duration-200 hover:text-emerald-100 hover:bg-white hover:bg-opacity-20"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="overflow-y-auto flex-1 p-6 bg-gray-50">
                <div className="space-y-6">
                  {/* Personal Information */}
                  <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
                    <div className="flex items-center mb-5">
                      <div className="p-2 mr-3 bg-blue-100 rounded-lg">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">Personal Information</h4>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="p-4 bg-white rounded-lg border border-blue-100">
                        <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                          Full Name
                        </label>
                        <p className="text-base font-semibold text-gray-900">
                          {selectedStudent.firstName} {selectedStudent.lastName}
                        </p>
                      </div>
                      {selectedStudent.gender && (
                        <div className="p-4 bg-white rounded-lg border border-blue-100">
                          <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                            Gender
                          </label>
                          <p className="text-base font-medium text-gray-900 capitalize">
                            {selectedStudent.gender}
                          </p>
                        </div>
                      )}
                      {selectedStudent.dob && (
                        <div className="p-4 bg-white rounded-lg border border-blue-100">
                          <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                            Date of Birth
                          </label>
                          <div className="flex items-center">
                            <Calendar className="mr-2 w-4 h-4 text-gray-400" />
                            <p className="text-base font-medium text-gray-900">
                              {selectedStudent.dob.length === 8
                                ? new Date(
                                    selectedStudent.dob.slice(0, 4),
                                    selectedStudent.dob.slice(4, 6) - 1,
                                    selectedStudent.dob.slice(6, 8)
                                  ).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                : new Date(selectedStudent.dob).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </p>
                          </div>
                        </div>
                      )}
                      {selectedStudent.studentId && (
                        <div className="p-4 bg-white rounded-lg border border-blue-100">
                          <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                            Student ID
                          </label>
                          <span className="text-sm font-mono bg-gray-100 text-gray-800 px-3 py-1.5 rounded-lg inline-block">
                            {selectedStudent.studentId}
                          </span>
                        </div>
                      )}
                      <div className="p-4 bg-white rounded-lg border border-blue-100">
                        <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                          Account Status
                        </label>
                        <div className="mt-1">
                          {getStatusBadge(selectedStudent)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 shadow-sm">
                    <div className="flex items-center mb-5">
                      <div className="p-2 mr-3 bg-purple-100 rounded-lg">
                        <Phone className="w-5 h-5 text-purple-600" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">Contact Information</h4>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {selectedStudent.email && (
                        <div className="p-4 bg-white rounded-lg border border-purple-100">
                          <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                            Email Address
                          </label>
                          <div className="flex items-center">
                            <Mail className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                            <p className="text-sm font-medium text-gray-900 break-all">
                              {selectedStudent.email}
                            </p>
                          </div>
                        </div>
                      )}
                      {selectedStudent.phone && (
                        <div className="p-4 bg-white rounded-lg border border-purple-100">
                          <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                            Phone Number
                          </label>
                          <div className="flex items-center">
                            <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                            <p className="text-sm font-medium text-gray-900">
                              {selectedStudent.phone}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location Information */}
                  {(selectedStudent.location || selectedStudent.region) && (
                    <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
                      <div className="flex items-center mb-5">
                        <div className="p-2 mr-3 bg-green-100 rounded-lg">
                          <MapPin className="w-5 h-5 text-green-600" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-900">Location Information</h4>
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {selectedStudent.location && (
                          <div className="p-4 bg-white rounded-lg border border-green-100">
                            <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                              Location
                            </label>
                            <div className="flex items-center">
                              <MapPin className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                              <p className="text-sm font-medium text-gray-900">
                                {selectedStudent.location}
                              </p>
                            </div>
                          </div>
                        )}
                        {selectedStudent.region && (
                          <div className="p-4 bg-white rounded-lg border border-green-100">
                            <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                              Region
                            </label>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedStudent.region}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* School Information */}
                  {selectedStudent.schoolName && (
                    <div className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 shadow-sm">
                      <div className="flex items-center mb-5">
                        <div className="p-2 mr-3 bg-amber-100 rounded-lg">
                          <BookOpen className="w-5 h-5 text-amber-600" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-900">School Information</h4>
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="p-4 bg-white rounded-lg border border-amber-100">
                          <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                            School Name
                          </label>
                          <p className="text-sm font-semibold text-gray-900">
                            {selectedStudent.schoolName}
                          </p>
                        </div>
                        {selectedStudent.studentCardIssueDate && (
                          <div className="p-4 bg-white rounded-lg border border-amber-100">
                            <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                              Card Issue Date
                            </label>
                            <div className="flex items-center">
                              <Calendar className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                              <p className="text-sm font-medium text-gray-900">
                                {new Date(
                                  selectedStudent.studentCardIssueDate
                                ).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </p>
                            </div>
                          </div>
                        )}
                        {selectedStudent.studentCardExpiryDate && (
                          <div className="p-4 bg-white rounded-lg border border-amber-100">
                            <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                              Card Expiry Date
                            </label>
                            <div className="flex items-center">
                              <Calendar className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                              <p className="text-sm font-medium text-gray-900">
                                {new Date(
                                  selectedStudent.studentCardExpiryDate
                                ).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Agent Information */}
                  {selectedStudent.agentCode && (
                    <div className="p-6 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-200 shadow-sm">
                      <div className="flex items-center mb-5">
                        <div className="p-2 mr-3 bg-indigo-100 rounded-lg">
                          <User className="w-5 h-5 text-indigo-600" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-900">Registering Agent</h4>
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {selectedStudent.agentName && (
                          <div className="p-4 bg-white rounded-lg border border-indigo-100">
                            <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                              Agent Name
                            </label>
                            <p className="text-sm font-semibold text-gray-900">
                              {selectedStudent.agentName}
                            </p>
                          </div>
                        )}
                        {selectedStudent.agentCode && (
                          <div className="p-4 bg-white rounded-lg border border-indigo-100">
                            <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                              Agent Code
                            </label>
                            <span className="text-sm font-mono bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-lg inline-block font-semibold">
                              {selectedStudent.agentCode}
                            </span>
                          </div>
                        )}
                        {selectedStudent.agentEmail && (
                          <div className="p-4 bg-white rounded-lg border border-indigo-100">
                            <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                              Agent Email
                            </label>
                            <div className="flex items-center">
                              <Mail className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                              <p className="text-sm font-medium text-gray-900 break-all">
                                {selectedStudent.agentEmail}
                              </p>
                            </div>
                          </div>
                        )}
                        {selectedStudent.agentPhone && (
                          <div className="p-4 bg-white rounded-lg border border-indigo-100">
                            <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                              Agent Phone
                            </label>
                            <div className="flex items-center">
                              <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                              <p className="text-sm font-medium text-gray-900">
                                {selectedStudent.agentPhone}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Guardian Information */}
                  {(selectedStudent.guardianName || selectedStudent.guardianPhone) && (
                    <div className="p-6 bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl border border-rose-200 shadow-sm">
                      <div className="flex items-center mb-5">
                        <div className="p-2 mr-3 bg-rose-100 rounded-lg">
                          <Shield className="w-5 h-5 text-rose-600" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-900">Guardian Information</h4>
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {selectedStudent.guardianName && (
                          <div className="p-4 bg-white rounded-lg border border-rose-100">
                            <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                              Guardian Name
                            </label>
                            <p className="text-sm font-semibold text-gray-900">
                              {selectedStudent.guardianName}
                            </p>
                          </div>
                        )}
                        {selectedStudent.guardianPhone && (
                          <div className="p-4 bg-white rounded-lg border border-rose-100">
                            <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                              Guardian Phone
                            </label>
                            <div className="flex items-center">
                              <Phone className="flex-shrink-0 mr-2 w-4 h-4 text-gray-400" />
                              <p className="text-sm font-medium text-gray-900">
                                {selectedStudent.guardianPhone}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end px-6 py-4 bg-white border-t border-gray-200 shadow-inner">
                <button
                  onClick={closeViewModal}
                  className="px-8 py-2.5 bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-lg hover:from-emerald-700 hover:to-blue-700 transition-all duration-200 font-semibold shadow-md hover:shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Activation/Deactivation Confirmation Modal */}
        {showActivateModal && studentToActivate && (
          <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
              onClick={activating ? undefined : closeActivateModal}
            />

            {/* Modal */}
            <div className="overflow-hidden relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl duration-200 animate-in fade-in-0 zoom-in-95">
              {/* Header */}
              <div className={`px-6 py-5 bg-gradient-to-r ${
                isDeactivating 
                  ? "from-red-500 to-rose-600" 
                  : "from-green-500 to-emerald-600"
              }`}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="flex justify-center items-center w-12 h-12 bg-white bg-opacity-20 rounded-full border-2 border-white border-opacity-30 backdrop-blur-sm">
                      <Power className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {isDeactivating ? "Deactivate Student" : "Activate Student"}
                      </h3>
                      <p className={`text-sm mt-0.5 ${
                        isDeactivating ? "text-red-100" : "text-green-100"
                      }`}>
                        {isDeactivating ? "Confirm Deactivation" : "Confirm Activation"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeActivateModal}
                    disabled={activating}
                    className={`p-2 text-white rounded-lg transition-all duration-200 hover:bg-white hover:bg-opacity-20 disabled:opacity-50 ${
                      isDeactivating ? "hover:text-red-100" : "hover:text-green-100"
                    }`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 bg-gray-50">
                <div className="mb-6">
                  <div className="flex justify-center items-center mb-4">
                    <div className={`p-4 rounded-full ${
                      isDeactivating ? "bg-red-100" : "bg-green-100"
                    }`}>
                      <AlertTriangle className={`w-8 h-8 ${
                        isDeactivating ? "text-red-600" : "text-green-600"
                      }`} />
                    </div>
                  </div>
                  <h4 className="mb-3 text-lg font-semibold text-center text-gray-900">
                    {isDeactivating ? "Deactivate Student Account?" : "Activate Student Account?"}
                  </h4>
                  <p className="text-sm leading-relaxed text-center text-gray-600">
                    Are you sure you want to{" "}
                    <span className={`font-semibold ${
                      isDeactivating ? "text-red-600" : "text-green-600"
                    }`}>
                      {isDeactivating ? "deactivate" : "activate"}
                    </span>{" "}
                    the account for{" "}
                    <span className="font-semibold text-gray-900">
                      {studentToActivate.firstName} {studentToActivate.lastName}
                    </span>
                    ?
                    {isDeactivating && (
                      <>
                        <br />
                        <br />
                        This will disable the student's access to their account and prevent them from performing authorized actions.
                      </>
                    )}
                    {!isDeactivating && (
                      <>
                        <br />
                        <br />
                        This will enable the student to access their account and perform authorized actions.
                      </>
                    )}
                  </p>
                </div>

                {/* Student Info Card */}
                <div className={`p-4 mb-6 bg-white rounded-xl border-2 shadow-sm ${
                  isDeactivating ? "border-red-200" : "border-green-200"
                }`}>
                  <div className="flex items-center space-x-4">
                    <div className="flex justify-center items-center w-14 h-14 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full shadow-md">
                      <span className="text-lg font-bold text-white">
                        {studentToActivate.firstName?.charAt(0) || "S"}
                        {studentToActivate.lastName?.charAt(0) || "T"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-semibold text-gray-900">
                        {studentToActivate.firstName} {studentToActivate.lastName}
                      </div>
                      {studentToActivate.email && (
                        <div className="mt-1 text-sm text-gray-600 truncate">
                          {studentToActivate.email}
                        </div>
                      )}
                      {studentToActivate.studentId && (
                        <div className="mt-1 font-mono text-xs text-gray-500">
                          ID: {studentToActivate.studentId}
                        </div>
                      )}
                      {studentToActivate.schoolName && (
                        <div className="mt-1 text-xs text-gray-500 truncate">
                          {studentToActivate.schoolName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="pt-4 mt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        Current Status
                      </span>
                      {getStatusBadge(studentToActivate)}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <button
                    onClick={closeActivateModal}
                    disabled={activating}
                    className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-lg transition-all duration-200 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmActivate}
                    disabled={activating}
                    className={`flex flex-1 justify-center items-center px-4 py-3 font-semibold text-white rounded-lg shadow-md transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDeactivating
                        ? "bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700"
                        : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    }`}
                  >
                    {activating ? (
                      <>
                        <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                        {isDeactivating ? "Deactivating..." : "Activating..."}
                      </>
                    ) : (
                      <>
                        <Power className="mr-2 w-4 h-4" />
                        {isDeactivating ? "Deactivate Student" : "Activate Student"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Student Modal */}
        {showEditModal && studentToEdit && (
          <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
              onClick={isSubmitting ? undefined : closeEditModal}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden my-8">
              {/* Header */}
              <div className="px-6 py-5 bg-gradient-to-r from-blue-500 to-indigo-600">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="flex justify-center items-center w-12 h-12 bg-white bg-opacity-20 rounded-full border-2 border-white border-opacity-30 backdrop-blur-sm">
                      <Pencil className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Edit Student
                      </h3>
                      <p className="text-blue-100 text-sm mt-0.5">
                        Update student information
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeEditModal}
                    disabled={isSubmitting}
                    className="p-2 text-white rounded-lg transition-all duration-200 hover:text-blue-100 hover:bg-white hover:bg-opacity-20 disabled:opacity-50"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="overflow-y-auto flex-1 p-6 bg-gray-50">
                <div className="space-y-6">
                  {/* Personal Information */}
                  <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <h4 className="flex items-center mb-5 text-lg font-bold text-gray-900">
                      <User className="mr-2 w-5 h-5 text-blue-600" />
                      Personal Information
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">First Name *</label>
                        <input
                          type="text"
                          value={editFormData.firstName}
                          onChange={(e) => handleEditInputChange("firstName", e.target.value)}
                          className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Last Name *</label>
                        <input
                          type="text"
                          value={editFormData.lastName}
                          onChange={(e) => handleEditInputChange("lastName", e.target.value)}
                          className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Email *</label>
                        <input
                          type="email"
                          value={editFormData.email}
                          onChange={(e) => handleEditInputChange("email", e.target.value)}
                          className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Phone Number *</label>
                        <input
                          type="tel"
                          value={editFormData.phoneDisplay}
                          onChange={(e) => handleEditPhoneChange(e.target.value)}
                          placeholder="0XXXXXXXXX"
                          className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Gender *</label>
                        <div className="flex gap-3">
                          {["Male", "Female"].map((option) => (
                            <label
                              key={option}
                              className={`flex items-center gap-2 px-4 py-3 flex-1 rounded-lg border-2 cursor-pointer transition-all ${
                                editFormData.gender.toLowerCase() === option.toLowerCase()
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 bg-white hover:border-gray-300"
                              }`}
                            >
                              <input
                                type="radio"
                                name="gender"
                                value={option}
                                checked={editFormData.gender.toLowerCase() === option.toLowerCase()}
                                onChange={() => handleEditInputChange("gender", option)}
                                className="w-4 h-4 text-blue-600"
                              />
                              <span className={`text-sm font-medium ${
                                editFormData.gender.toLowerCase() === option.toLowerCase()
                                  ? "text-blue-700"
                                  : "text-gray-700"
                              }`}>
                                {option}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Date of Birth *</label>
                        <input
                          type="date"
                          value={editFormData.dob}
                          onChange={(e) => handleEditInputChange("dob", e.target.value)}
                          className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Region *</label>
                        <SearchableSelect
                          options={ghanaRegions}
                          value={editFormData.region}
                          onChange={(value) => {
                            handleEditInputChange("region", value);
                            handleEditInputChange("location", "");
                            handleEditInputChange("customLocation", "");
                          }}
                          placeholder="Select Region"
                          label="region"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Location *</label>
                        {editFormData.region ? (
                          <>
                            <SearchableSelect
                              options={ghanaLocationsByRegion[editFormData.region] || []}
                              value={editFormData.location}
                              onChange={(value) => {
                                if (value === "Other") {
                                  handleEditInputChange("location", "Other");
                                } else {
                                  handleEditInputChange("location", value);
                                  handleEditInputChange("customLocation", "");
                                }
                              }}
                              placeholder="Select Location"
                              showOther={true}
                              otherPlaceholder="Enter custom location"
                              label="location"
                            />
                            {editFormData.location === "Other" && (
                              <input
                                type="text"
                                value={editFormData.customLocation}
                                onChange={(e) => handleEditInputChange("customLocation", e.target.value)}
                                placeholder="Enter custom location"
                                className="px-4 py-3 mt-2 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            )}
                          </>
                        ) : (
                          <input
                            type="text"
                            value={editFormData.location}
                            onChange={(e) => handleEditInputChange("location", e.target.value)}
                            placeholder="Select region first"
                            disabled
                            className="px-4 py-3 w-full bg-gray-100 rounded-lg border-2 border-gray-200 cursor-not-allowed"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Student Information */}
                  <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <h4 className="flex items-center mb-5 text-lg font-bold text-gray-900">
                      <GraduationCap className="mr-2 w-5 h-5 text-blue-600" />
                      Student Information
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">School Name</label>
                        <input
                          type="text"
                          value={editFormData.schoolName}
                          onChange={(e) => handleEditInputChange("schoolName", e.target.value)}
                          placeholder="e.g. University of Ghana"
                          className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Student ID</label>
                        <input
                          type="text"
                          value={editFormData.studentId}
                          onChange={(e) => handleEditInputChange("studentId", e.target.value)}
                          placeholder="e.g. UG-2024-001"
                          className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Student Card Issue Date</label>
                        <input
                          type="date"
                          value={editFormData.studentCardIssueDate}
                          onChange={(e) => handleEditInputChange("studentCardIssueDate", e.target.value)}
                          className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-700">Student Card Expiry Date</label>
                        <input
                          type="date"
                          value={editFormData.studentCardExpiryDate}
                          onChange={(e) => handleEditInputChange("studentCardExpiryDate", e.target.value)}
                          className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Student Card Images */}
                    <div className="mt-6">
                      <label className="flex gap-2 items-center mb-4 text-sm font-medium text-gray-700">
                        <Shield className="w-4 h-4" />
                        Student Card Images
                      </label>
                      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div>
                          <label className="block mb-3 text-sm font-medium text-gray-700">Front</label>
                          {editFormData.studentCardFront ? (
                            <div className="relative">
                              <img
                                src={editFormData.studentCardFront}
                                alt="Student card front"
                                className="object-cover w-full h-48 rounded-lg border-2 border-gray-200"
                              />
                              <button
                                onClick={() => removeImage("studentCardFront")}
                                className="flex absolute -top-2 -right-2 justify-center items-center w-8 h-8 text-white bg-red-500 rounded-full hover:bg-red-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <label className="absolute bottom-2 left-2 block px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg cursor-pointer hover:bg-blue-700">
                                <Upload className="inline mr-1 w-3 h-3" />
                                Change
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => e.target.files[0] && handleImageUpload("studentCardFront", e.target.files[0])}
                                  className="hidden"
                                />
                              </label>
                            </div>
                          ) : (
                            <label className="block w-full h-48 rounded-lg border-2 border-gray-300 border-dashed cursor-pointer hover:border-blue-400">
                              <div className="flex flex-col justify-center items-center h-full text-gray-600">
                                <Upload className="mb-2 w-8 h-8" />
                                <span className="text-sm">Upload front image</span>
                              </div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => e.target.files[0] && handleImageUpload("studentCardFront", e.target.files[0])}
                                className="hidden"
                              />
                            </label>
                          )}
                        </div>
                        <div>
                          <label className="block mb-3 text-sm font-medium text-gray-700">Back</label>
                          {editFormData.studentCardBack ? (
                            <div className="relative">
                              <img
                                src={editFormData.studentCardBack}
                                alt="Student card back"
                                className="object-cover w-full h-48 rounded-lg border-2 border-gray-200"
                              />
                              <button
                                onClick={() => removeImage("studentCardBack")}
                                className="flex absolute -top-2 -right-2 justify-center items-center w-8 h-8 text-white bg-red-500 rounded-full hover:bg-red-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <label className="absolute bottom-2 left-2 block px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg cursor-pointer hover:bg-blue-700">
                                <Upload className="inline mr-1 w-3 h-3" />
                                Change
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => e.target.files[0] && handleImageUpload("studentCardBack", e.target.files[0])}
                                  className="hidden"
                                />
                              </label>
                            </div>
                          ) : (
                            <label className="block w-full h-48 rounded-lg border-2 border-gray-300 border-dashed cursor-pointer hover:border-blue-400">
                              <div className="flex flex-col justify-center items-center h-full text-gray-600">
                                <Upload className="mb-2 w-8 h-8" />
                                <span className="text-sm">Upload back image</span>
                              </div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => e.target.files[0] && handleImageUpload("studentCardBack", e.target.files[0])}
                                className="hidden"
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 justify-end px-6 py-4 bg-white border-t border-gray-200">
                <button
                  onClick={closeEditModal}
                  disabled={isSubmitting}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white rounded-lg border-2 border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={isSubmitting || !isFormValid()}
                  className="flex gap-2 items-center px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg transition-all hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Student"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisteredStudents;
